/**
 * ClickReel SDK - TikTok Player Overlay
 *
 * Embeds TikTok videos via iframe embed URL.
 * Custom play/pause overlay since TikTok embed has limited API.
 */

import {
  VideoProgressData,
  BubblePosition,
  StylingConfig,
  FloatingAction,
  ProductCardConfig,
  SocialProofConfig,
} from './types';
import { getShadowContainer } from './shadow-dom';
import { ProductCard } from './product-card';
import { injectUtilityStyles, isMobile } from './video-utils';
import { buildCtaUrl } from './analytics-bridge';

// =============================================================================
// TikTok Player Component
// =============================================================================

export interface TikTokPlayerOptions {
  /** TikTok video ID for embed */
  videoId: string;
  /** Original TikTok video URL (used for oEmbed resolution) */
  videoUrl?: string;
  /** API base URL for server-side resolution */
  apiUrl?: string;
  position: BubblePosition;
  styling: StylingConfig;
  onPlay?: () => void;
  onPause?: () => void;
  onProgress?: (data: VideoProgressData) => void;
  onComplete?: () => void;
  onClose?: () => void;
  onCtaClick?: () => void;
  onFloatingActionClick?: (action: FloatingAction) => void;
  ctaText?: string;
  ctaUrl?: string;
  floatingActions?: FloatingAction[];
  muteOnOpen?: boolean;
  productCard?: ProductCardConfig;
  socialProof?: SocialProofConfig;
  storyMode?: boolean;
  storyIndex?: number;
  storyTotal?: number;
  onStoryAdvance?: (direction: 'next' | 'prev') => void;
  showBranding?: boolean;
  estimatedDuration?: number;
  campaignName?: string;
  sessionId?: string;
  videoIdForTracking?: string;
}

/**
 * TikTok player overlay component using iframe embed
 */
export class TikTokPlayer {
  private overlay: HTMLElement | null = null;
  private container: HTMLElement | null = null;
  private options: TikTokPlayerOptions;
  private isOpen = false;
  private closing = false;
  private destroyed = false;
  // Sprint 005 (2026-04-29): visibility-aware progress tracking. When the
  // page is hidden during playback, browsers pause the iframe but Date.now()
  // continues advancing. Without these fields the elapsed counter would
  // inflate during background tab time, firing quartile events (and intent
  // score increments) for time the user wasn't actually watching.
  private visibilityHandler: (() => void) | null = null;
  private pausedAt: number = 0;
  private resolvedVideoId: string | null = null;
  private productCardInstance: ProductCard | null = null;
  private progressInterval: number | null = null;
  private progressStartTime: number = 0;
  private lastQuartile: number = 0;

  constructor(options: TikTokPlayerOptions) {
    this.options = options;
  }

  /**
   * Open the video player
   */
  async open(): Promise<void> {
    if (this.isOpen) return;

    const shadow = getShadowContainer();
    if (!shadow) {
      throw new Error('Shadow DOM not initialized');
    }

    // If videoId is not numeric, try to resolve it via server-side oEmbed
    if (!this.isNumericId(this.options.videoId)) {
      this.renderLoading(shadow);
      this.isOpen = true;

      const resolved = await this.resolveShortUrl();
      if (resolved) {
        this.resolvedVideoId = resolved;
        // Remove loading overlay and render full player
        this.overlay?.remove();
        this.overlay = null;
        this.container = null;
        this.isOpen = false;
        this.render(shadow);
        this.isOpen = true;
      } else {
        // Resolution failed — replace loading with branded placeholder
        this.overlay?.remove();
        this.overlay = null;
        this.container = null;
        this.isOpen = false;
        this.renderPlaceholder(shadow);
        this.isOpen = true;
      }
      return;
    }

    this.render(shadow);
    this.isOpen = true;
  }

  /**
   * Close the video player
   */
  close(): void {
    if (!this.isOpen || !this.overlay) return;
    this.closing = true;
    this.stopProgressTracking();

    // Animate out
    this.overlay.classList.add('closing');

    setTimeout(() => {
      this.destroy();
      this.options.onClose?.();
    }, 200);
  }

  /**
   * Check if player is currently open
   */
  get isVisible(): boolean {
    return this.isOpen;
  }

  /**
   * Destroy the player and clean up
   */
  destroy(): void {
    this.destroyed = true;
    this.closing = true;
    this.stopProgressTracking();
    this.productCardInstance?.destroy();
    this.lastQuartile = 0;
    this.progressStartTime = 0;

    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      this.container = null;
    }
    this.isOpen = false;

    // Remove escape key handler
    document.removeEventListener('keydown', this.handleEscapeKey);
  }

  // ===========================================================================
  // Private Methods - Rendering
  // ===========================================================================

  private render(shadow: ShadowRoot): void {
    const mobile = isMobile();

    // Create overlay backdrop
    this.overlay = document.createElement('div');
    this.overlay.className = 'clickreel-video-overlay clickreel-tiktok-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'TikTok video player');

    // Click outside to close (on backdrop)
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Create container
    this.container = document.createElement('div');
    this.container.className = `clickreel-video-container ${mobile ? 'mobile' : 'desktop'}`;

    // Apply position-based styles for desktop PiP
    if (!mobile) {
      this.applyPiPStyles();
    }

    // Build player content
    this.container.appendChild(this.createCloseButton());

    // Create video container
    const videoWrapper = this.createVideoContainer();

    // Add floating actions INSIDE the video wrapper so they overlay the video
    if (this.options.floatingActions && this.options.floatingActions.length > 0) {
      videoWrapper.appendChild(this.createFloatingActions());
    }

    // Stories mode UI
    this.createStoryUI(videoWrapper);

    // Product card slide-up
    if (this.options.productCard?.enabled) {
      this.productCardInstance = new ProductCard(this.options.productCard, this.options.styling.primaryColor);
      this.productCardInstance.render(videoWrapper);
    }

    // Social proof badge
    this.createSocialProof(videoWrapper);

    this.container.appendChild(videoWrapper);

    // Add mute/unmute control on desktop
    if (!mobile) {
      this.container.appendChild(this.createVideoControls());
    }

    if (this.options.showBranding) {
      const watermark = document.createElement('a');
      watermark.href = 'https://clickreel.io?utm_source=widget&utm_medium=referral&utm_campaign=powered_by';
      watermark.target = '_blank';
      watermark.rel = 'noopener';
      watermark.textContent = 'Powered by ClickReel.io';
      watermark.className = 'clickreel-player-watermark';
      videoWrapper.appendChild(watermark);
    }

    // Only show CTA on desktop if provided
    if (!mobile && this.options.ctaText && this.options.ctaUrl) {
      this.container.appendChild(this.createCTAButton());
    }

    this.overlay.appendChild(this.container);

    // Inject styles
    this.injectStyles(shadow);

    shadow.appendChild(this.overlay);

    // Handle escape key
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  private createCloseButton(): HTMLElement {
    const button = document.createElement('button');
    button.className = 'clickreel-close-btn';
    button.setAttribute('aria-label', 'Close video');
    button.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.3 5.71a.996.996 0 00-1.41 0L12 10.59 7.11 5.7A.996.996 0 105.7 7.11L10.59 12 5.7 16.89a.996.996 0 101.41 1.41L12 13.41l4.89 4.89a.996.996 0 101.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/>
      </svg>
    `;

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      this.close();
    });

    return button;
  }

  private createVideoContainer(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'clickreel-video-wrapper';
    wrapper.setAttribute('data-orientation', 'portrait');

    wrapper.appendChild(this.createEmbedIframe());

    return wrapper;
  }

  private isNumericId(id: string): boolean {
    return /^\d+$/.test(id);
  }

  private createEmbedIframe(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'clickreel-tiktok-embed-container';

    const videoId = this.resolvedVideoId || this.options.videoId;

    // Create iframe with TikTok embed URL (numeric ID only)
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.tiktok.com/embed/v2/${videoId}`;
    iframe.className = 'clickreel-tiktok-iframe';
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.allow = 'autoplay; encrypted-media';
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-popups allow-presentation',
    );

    // Add loading indicator
    const loader = document.createElement('div');
    loader.className = 'clickreel-tiktok-loader';
    loader.innerHTML = `
      <div class="clickreel-tiktok-loader-spinner"></div>
      <span>Loading TikTok...</span>
    `;

    iframe.addEventListener('load', () => {
      loader.style.display = 'none';
      this.options.onPlay?.();
      this.startProgressTracking();
    });

    container.appendChild(loader);
    container.appendChild(iframe);

    // Add TikTok branding bar (no external links)
    container.appendChild(this.createBrandingBar());

    return container;
  }

  private createBrandingBar(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'clickreel-tiktok-branding-bar';

    bar.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/>
      </svg>
      <span class="clickreel-tiktok-branding-text">TikTok</span>
    `;

    return bar;
  }

  private createVideoControls(): HTMLElement {
    const controls = document.createElement('div');
    controls.className = 'clickreel-video-controls clickreel-tiktok-controls';

    // TikTok branding label (no external link)
    const brandLabel = document.createElement('div');
    brandLabel.className = 'clickreel-tiktok-brand-label';
    brandLabel.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/>
      </svg>
      <span>TikTok</span>
    `;

    controls.appendChild(brandLabel);

    return controls;
  }

  private createCTAButton(): HTMLElement {
    const { styling } = this.options;

    const cta = document.createElement('a');
    cta.className = 'clickreel-cta-btn';
    cta.href = buildCtaUrl(this.options.ctaUrl, {
      campaignName: this.options.campaignName,
      videoId: this.options.videoIdForTracking,
      sessionId: this.options.sessionId,
    });
    cta.target = '_blank';
    cta.rel = 'noopener noreferrer';
    cta.textContent = this.options.ctaText || 'Learn More';
    cta.style.backgroundColor = styling.primaryColor || '#fe2c55'; // TikTok red

    cta.addEventListener('click', () => {
      this.options.onCtaClick?.();
    });

    return cta;
  }

  private createFloatingActions(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'clickreel-floating-actions';

    this.options.floatingActions?.forEach((action) => {
      const actionBtn = document.createElement('a');
      actionBtn.className = 'clickreel-floating-action';
      actionBtn.href = action.url;
      actionBtn.target = '_blank';
      actionBtn.rel = 'noopener noreferrer';

      // Left side: Shopping bag icon with pulse animation
      const iconWrapper = document.createElement('div');
      iconWrapper.className = 'clickreel-floating-action-icon-wrap';

      if (action.imageUrl) {
        const img = document.createElement('img');
        img.src = action.imageUrl;
        img.alt = action.label;
        img.className = 'clickreel-floating-action-img';
        iconWrapper.appendChild(img);
      } else {
        const icon = document.createElement('div');
        icon.className = 'clickreel-floating-action-icon';
        icon.innerHTML = `
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zm-9-1a2 2 0 0 1 4 0v1h-4V6zm8 13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v10z"/>
          </svg>
        `;
        iconWrapper.appendChild(icon);
      }
      actionBtn.appendChild(iconWrapper);

      // Center: Product info
      const textContainer = document.createElement('div');
      textContainer.className = 'clickreel-floating-action-text';

      const label = document.createElement('span');
      label.className = 'clickreel-floating-action-label';
      label.textContent = action.label;
      textContainer.appendChild(label);

      if (action.subtitle) {
        const subtitle = document.createElement('span');
        subtitle.className = 'clickreel-floating-action-subtitle';
        subtitle.textContent = action.subtitle;
        textContainer.appendChild(subtitle);
      }

      actionBtn.appendChild(textContainer);

      // Variant picker (color/size swatches)
      this.createVariantPicker(action, actionBtn);

      // Right side: Shop Now button
      const shopBtn = document.createElement('span');
      shopBtn.className = 'clickreel-floating-action-btn';
      shopBtn.textContent = 'Shop';
      actionBtn.appendChild(shopBtn);

      actionBtn.addEventListener('click', () => {
        this.options.onFloatingActionClick?.(action);
      });

      container.appendChild(actionBtn);
    });

    return container;
  }

  private createStoryUI(wrapper: HTMLElement): void {
    const { storyMode, storyIndex = 0, storyTotal = 1 } = this.options;
    if (!storyMode || storyTotal <= 1) return;

    const bar = document.createElement('div');
    bar.className = 'clickreel-story-bar';
    for (let i = 0; i < storyTotal; i++) {
      const seg = document.createElement('div');
      seg.className = 'clickreel-story-segment';
      if (i < storyIndex) seg.classList.add('completed');
      else if (i === storyIndex) {
        seg.classList.add('active');
        const fill = document.createElement('div');
        fill.className = 'clickreel-story-fill';
        seg.appendChild(fill);
      }
      bar.appendChild(seg);
    }
    wrapper.appendChild(bar);

    const counter = document.createElement('div');
    counter.className = 'clickreel-story-counter';
    counter.textContent = `${storyIndex + 1} / ${storyTotal}`;
    wrapper.appendChild(counter);

    const navLeft = document.createElement('div');
    navLeft.className = 'clickreel-story-nav clickreel-story-nav-left';
    navLeft.innerHTML = '<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg></div>';
    navLeft.addEventListener('click', (e) => { e.stopPropagation(); this.options.onStoryAdvance?.('prev'); });
    wrapper.appendChild(navLeft);

    const navRight = document.createElement('div');
    navRight.className = 'clickreel-story-nav clickreel-story-nav-right';
    navRight.innerHTML = '<div class="clickreel-story-chevron"><svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg></div>';
    navRight.addEventListener('click', (e) => { e.stopPropagation(); this.options.onStoryAdvance?.('next'); });
    wrapper.appendChild(navRight);
  }

  private createSocialProof(wrapper: HTMLElement): void {
    if (!this.options.socialProof?.enabled) return;
    const badge = document.createElement('div');
    const pos = this.options.socialProof.position || 'top-left';
    badge.className = `clickreel-social-proof ${pos}`;
    badge.textContent = this.options.socialProof.text;
    wrapper.appendChild(badge);
  }

  private createVariantPicker(action: FloatingAction, actionBtn: HTMLAnchorElement): void {
    if (!action.variants || action.variants.length === 0) return;
    const picker = document.createElement('div');
    picker.className = 'clickreel-variant-picker';
    const maxShow = 6;
    const variants = action.variants.slice(0, maxShow);

    variants.forEach((v) => {
      if (v.type === 'color') {
        const swatch = document.createElement('div');
        swatch.className = 'clickreel-variant-swatch';
        if (v.available === false) swatch.classList.add('unavailable');
        swatch.style.background = v.colorHex || '#888';
        swatch.addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation();
          if (v.available === false) return;
          picker.querySelectorAll('.clickreel-variant-swatch').forEach(s => s.classList.remove('selected'));
          swatch.classList.add('selected');
          this.updateActionUrl(actionBtn, action, v.id);
          this.updateActionLabel(actionBtn, action, v.label);
        });
        picker.appendChild(swatch);
      } else {
        const chip = document.createElement('button');
        chip.className = 'clickreel-variant-chip';
        if (v.available === false) chip.classList.add('unavailable');
        chip.textContent = v.label;
        chip.addEventListener('click', (e) => {
          e.preventDefault(); e.stopPropagation();
          if (v.available === false) return;
          picker.querySelectorAll('.clickreel-variant-chip').forEach(c => c.classList.remove('selected'));
          chip.classList.add('selected');
          this.updateActionUrl(actionBtn, action, v.id);
          this.updateActionLabel(actionBtn, action, v.label);
        });
        picker.appendChild(chip);
      }
    });

    if (action.variants.length > maxShow) {
      const more = document.createElement('span');
      more.className = 'clickreel-variant-more';
      more.textContent = `+${action.variants.length - maxShow} more`;
      picker.appendChild(more);
    }

    const textEl = actionBtn.querySelector('.clickreel-floating-action-text');
    if (textEl) actionBtn.insertBefore(picker, textEl);
    else actionBtn.appendChild(picker);
  }

  private updateActionUrl(btn: HTMLAnchorElement, action: FloatingAction, variantId: string): void {
    try {
      const param = action.selectedVariantParam || 'v';
      const url = new URL(action.url);
      url.searchParams.set(param, variantId);
      btn.href = url.toString();
    } catch { /* invalid URL */ }
  }

  private updateActionLabel(btn: HTMLAnchorElement, action: FloatingAction, variantLabel: string): void {
    const label = btn.querySelector('.clickreel-floating-action-label');
    if (label) label.textContent = `${action.label} — ${variantLabel}`;
  }

  private applyPiPStyles(): void {
    if (!this.container) return;

    const { position } = this.options;
    const margin = '24px';

    let positionStyle = '';
    switch (position) {
      case 'bottom-right':
        positionStyle = `bottom: ${margin}; right: ${margin};`;
        break;
      case 'bottom-left':
        positionStyle = `bottom: ${margin}; left: ${margin};`;
        break;
      case 'top-right':
        positionStyle = `top: ${margin}; right: ${margin};`;
        break;
      case 'top-left':
        positionStyle = `top: ${margin}; left: ${margin};`;
        break;
    }

    this.container.style.cssText = positionStyle;
  }

  /**
   * Resolve a TikTok short URL to a numeric video ID via server-side oEmbed proxy
   */
  private async resolveShortUrl(): Promise<string | null> {
    const videoUrl = this.options.videoUrl
      || `https://www.tiktok.com/t/${this.options.videoId}`;
    const apiUrl = this.options.apiUrl || '';

    try {
      const response = await fetch(
        `${apiUrl}/v1/tiktok/resolve?url=${encodeURIComponent(videoUrl)}`,
      );
      if (!response.ok) return null;
      const data = await response.json();
      return data.videoId || null;
    } catch {
      return null;
    }
  }

  /**
   * Render a loading overlay while resolving short URL
   */
  private renderLoading(shadow: ShadowRoot): void {
    const mobile = isMobile();

    this.overlay = document.createElement('div');
    this.overlay.className = 'clickreel-video-overlay clickreel-tiktok-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'Loading TikTok video');

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    this.container = document.createElement('div');
    this.container.className = `clickreel-video-container ${mobile ? 'mobile' : 'desktop'}`;

    if (!mobile) this.applyPiPStyles();

    this.container.appendChild(this.createCloseButton());

    const wrapper = document.createElement('div');
    wrapper.className = 'clickreel-video-wrapper';
    wrapper.setAttribute('data-orientation', 'portrait');

    const loadingCard = document.createElement('div');
    loadingCard.className = 'clickreel-tiktok-fallback-card';
    loadingCard.innerHTML = `
      <div class="clickreel-tiktok-fallback-logo">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/>
        </svg>
      </div>
      <div class="clickreel-tiktok-loader-spinner"></div>
      <h3 class="clickreel-tiktok-fallback-title">Loading video...</h3>
    `;

    wrapper.appendChild(loadingCard);
    this.container.appendChild(wrapper);
    this.overlay.appendChild(this.container);

    this.injectStyles(shadow);
    shadow.appendChild(this.overlay);

    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  /**
   * Render a branded placeholder when resolution fails (no external links)
   */
  private renderPlaceholder(shadow: ShadowRoot): void {
    const mobile = isMobile();

    this.overlay = document.createElement('div');
    this.overlay.className = 'clickreel-video-overlay clickreel-tiktok-overlay';
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'TikTok video unavailable');

    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) this.close();
    });

    this.container = document.createElement('div');
    this.container.className = `clickreel-video-container ${mobile ? 'mobile' : 'desktop'}`;

    if (!mobile) this.applyPiPStyles();

    this.container.appendChild(this.createCloseButton());

    const wrapper = document.createElement('div');
    wrapper.className = 'clickreel-video-wrapper';
    wrapper.setAttribute('data-orientation', 'portrait');

    const placeholderCard = document.createElement('div');
    placeholderCard.className = 'clickreel-tiktok-fallback-card';
    placeholderCard.innerHTML = `
      <div class="clickreel-tiktok-fallback-logo">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z"/>
        </svg>
      </div>
      <h3 class="clickreel-tiktok-fallback-title">TikTok Video</h3>
      <p class="clickreel-tiktok-fallback-subtitle">This short URL couldn't be loaded automatically.</p>
      <p class="clickreel-tiktok-fallback-hint">Try using the full TikTok video URL<br/>(tiktok.com/@user/video/ID) in your campaign settings.</p>
    `;

    wrapper.appendChild(placeholderCard);
    this.container.appendChild(wrapper);
    this.overlay.appendChild(this.container);

    this.injectStyles(shadow);
    shadow.appendChild(this.overlay);

    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  private startProgressTracking(): void {
    this.stopProgressTracking();

    const duration = this.options.estimatedDuration || 300;
    this.progressStartTime = Date.now();
    this.lastQuartile = 0;
    this.pausedAt = 0;

    // Sprint 005 (2026-04-29): pause/resume the elapsed accumulator on tab
    // visibility changes. When the tab hides, capture pausedAt = Date.now().
    // When it returns to visible, shift progressStartTime forward by the
    // hidden duration so elapsed continues from where it left off.
    this.visibilityHandler = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        this.pausedAt = Date.now();
      } else if (this.pausedAt > 0) {
        const hiddenDuration = Date.now() - this.pausedAt;
        this.progressStartTime += hiddenDuration;
        this.pausedAt = 0;
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    this.progressInterval = window.setInterval(() => {
      if (this.destroyed || this.closing) {
        this.stopProgressTracking();
        return;
      }

      const elapsed = (Date.now() - this.progressStartTime) / 1000;
      const percent = Math.min(Math.floor((elapsed / duration) * 100), 100);

      const quartiles: Array<25 | 50 | 75> = [25, 50, 75];
      for (const q of quartiles) {
        if (percent >= q && this.lastQuartile < q) {
          this.emitProgress(q, elapsed, duration);
          this.lastQuartile = q;
        }
      }

      if (this.productCardInstance && !this.productCardInstance.hasShown) {
        if (percent >= this.productCardInstance.triggerPercent) {
          this.productCardInstance.show();
        }
      }

      if (percent >= 100) {
        this.emitProgress(100, duration, duration);
        this.stopProgressTracking();
        // Embed durations are heuristic estimates — we can't know when the
        // video actually ends. In story mode, advance to the next video.
        // In single-video mode, let the user close manually (no auto-close).
        if (this.options.storyMode && !this.closing && !this.destroyed) {
          this.options.onComplete?.();
          this.options.onStoryAdvance?.('next');
        }
      }
    }, 500);
  }

  private stopProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    // Sprint 005 (2026-04-29): unbind visibility listener; freezing/resuming
    // is only meaningful while a progress interval is active.
    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    this.pausedAt = 0;
  }

  private emitProgress(quartile: 0 | 25 | 50 | 75 | 100, currentTime: number, duration: number): void {
    const videoId = this.resolvedVideoId || this.options.videoId;
    const data: VideoProgressData = {
      videoId,
      currentTime: Math.round(currentTime * 100) / 100,
      duration,
      percent: Math.floor((currentTime / duration) * 100),
      quartile,
    };
    this.options.onProgress?.(data);
  }

  private handleEscapeKey(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  // ===========================================================================
  // Private Methods - Utilities
  // ===========================================================================

  private injectStyles(shadow: ShadowRoot): void {
    injectUtilityStyles(shadow);

    const existingStyle = shadow.querySelector('style[data-tiktok-player]');
    if (existingStyle) return;

    const style = document.createElement('style');
    style.setAttribute('data-tiktok-player', 'true');
    style.textContent = `
      /* ================================================================
         Base overlay/container styles (TikTok player is standalone,
         so it must include these — video-player.ts won't inject them)
         ================================================================ */

      .clickreel-video-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.75);
        display: flex;
        align-items: center;
        justify-content: center;
        animation: clickreel-fade-in 0.2s ease;
        pointer-events: auto;
        z-index: 2;
      }

      .clickreel-video-overlay.closing {
        animation: clickreel-fade-out 0.2s ease forwards;
      }

      .clickreel-video-container.desktop {
        position: absolute;
        width: 380px;
        max-width: 90vw;
        background: #000;
        border-radius: 16px;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
        animation: clickreel-slide-up 0.3s ease;
      }

      .clickreel-video-container.mobile {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        background: #000;
        display: flex;
        flex-direction: column;
      }

      .clickreel-close-btn {
        position: absolute;
        top: 12px;
        right: 12px;
        z-index: 100;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.5);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: background 0.2s;
        border: none;
        cursor: pointer;
        padding: 0;
      }

      .clickreel-close-btn:hover {
        background: rgba(0, 0, 0, 0.7);
      }

      /* Mobile: Larger touch target with contrast ring in safe area */
      .mobile .clickreel-close-btn {
        top: max(16px, env(safe-area-inset-top, 16px));
        right: max(16px, env(safe-area-inset-right, 16px));
        width: 44px;
        height: 44px;
        background: rgba(0, 0, 0, 0.6);
        border: 2px solid rgba(255, 255, 255, 0.4);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
      }

      .mobile .clickreel-close-btn:active {
        background: rgba(0, 0, 0, 0.8);
        border-color: rgba(255, 255, 255, 0.6);
      }

      .clickreel-video-wrapper {
        position: relative;
        width: 100%;
        background: #000;
      }

      .clickreel-video-wrapper[data-orientation="portrait"] {
        aspect-ratio: 9/16;
      }

      .mobile .clickreel-video-wrapper {
        flex: 1;
        display: flex;
        align-items: center;
        aspect-ratio: unset;
      }

      .clickreel-cta-btn {
        display: block;
        width: 100%;
        padding: 14px;
        text-align: center;
        color: white;
        font-weight: 600;
        font-size: 15px;
        text-decoration: none;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      }

      @keyframes clickreel-fade-in {
        from { opacity: 0; } to { opacity: 1; }
      }
      @keyframes clickreel-fade-out {
        from { opacity: 1; } to { opacity: 0; }
      }
      @keyframes clickreel-slide-up {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }

      /* ================================================================
         TikTok-specific styles
         ================================================================ */

      /* Floating actions z-index */
      .clickreel-floating-actions {
        z-index: 50;
        position: relative;
      }

      .clickreel-tiktok-embed-container {
        position: relative;
        width: 100%;
        height: 100%;
        background: #000;
        z-index: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      .clickreel-tiktok-iframe {
        width: 100%;
        flex: 1;
        max-width: 540px;
        min-height: 500px;
        border: none;
        background: #000;
        z-index: 1;
      }

      .mobile .clickreel-tiktok-embed-container {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 1;
      }

      .mobile .clickreel-tiktok-iframe {
        max-width: 100%;
        min-height: unset;
        height: 100%;
      }

      /* TikTok Loader */
      .clickreel-tiktok-loader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        color: #fff;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        z-index: 5;
      }

      .clickreel-tiktok-loader-spinner {
        width: 40px;
        height: 40px;
        border: 3px solid rgba(255, 255, 255, 0.2);
        border-top-color: #fe2c55;
        border-radius: 50%;
        animation: clickreel-tiktok-spin 0.8s linear infinite;
      }

      @keyframes clickreel-tiktok-spin {
        to { transform: rotate(360deg); }
      }

      /* TikTok Fallback Card (for non-numeric IDs / short URLs) */
      .clickreel-tiktok-fallback-card {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 16px;
        width: 100%;
        height: 100%;
        min-height: 400px;
        background: #000;
        color: #fff;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        text-align: center;
        padding: 32px;
      }

      .clickreel-tiktok-fallback-logo {
        width: 80px;
        height: 80px;
        border-radius: 20px;
        background: linear-gradient(135deg, #25f4ee 0%, #fe2c55 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 24px rgba(254, 44, 85, 0.4);
      }

      .clickreel-tiktok-fallback-title {
        font-size: 18px;
        font-weight: 600;
        margin: 4px 0 0;
      }

      .clickreel-tiktok-fallback-subtitle {
        font-size: 13px;
        color: #aaa;
        margin: 0;
      }

      .clickreel-tiktok-fallback-hint {
        font-size: 12px;
        color: #777;
        margin: 0;
        line-height: 1.5;
      }

      /* TikTok Branding Bar */
      .clickreel-tiktok-branding-bar {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: rgba(0, 0, 0, 0.9);
        width: 100%;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        z-index: 2;
      }

      .clickreel-tiktok-branding-text {
        color: #fff;
        font-size: 12px;
        font-weight: 500;
        opacity: 0.8;
      }

      /* TikTok controls styling */
      .clickreel-tiktok-controls {
        position: relative;
        opacity: 1;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px 12px;
      }

      .clickreel-tiktok-brand-label {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: #fff;
        font-size: 13px;
        font-weight: 500;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        opacity: 0.8;
      }

      /* TikTok brand color for CTA */
      .clickreel-tiktok-overlay .clickreel-cta-btn {
        background: #fe2c55;
      }

      .clickreel-tiktok-overlay .clickreel-cta-btn:hover {
        filter: brightness(1.1);
      }

      /* TikTok brand color for floating action icons */
      .clickreel-tiktok-overlay .clickreel-floating-action-icon {
        background: linear-gradient(135deg, #25f4ee 0%, #fe2c55 100%);
        box-shadow: 0 2px 8px rgba(254, 44, 85, 0.4);
      }

      .clickreel-player-watermark {
        position: absolute;
        bottom: 6px;
        left: 0;
        right: 0;
        text-align: center;
        font-size: 10px;
        color: rgba(255, 255, 255, 0.5);
        text-decoration: none;
        pointer-events: auto;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        transition: color 0.2s ease;
        z-index: 10;
      }
      .clickreel-player-watermark:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    `;
    shadow.appendChild(style);
  }
}
